import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { TriageData, TriageOutcome } from '@/types/triage';

// Enhanced Question structure for dynamic generation
export interface Question {
  id: string;
  text: string;
  inputType: 'radio' | 'text' | 'slider' | 'checkbox' | 'number';
  options?: { value: string; label: string }[];
  placeholder?: string;
  required?: boolean;
  category: 'demographics' | 'vitals' | 'medical_history' | 'medications' | 'allergies' | 'symptom_details';
  helpText?: string;
  conditionalOn?: string; // Shows question only if another answer matches
  conditionalValue?: string;
}

// Audio Triage State
export interface AudioTriageState {
  // Symptom Collection
  selectedSymptoms: string[];
  symptomsText: string;
  recordedBlob: Blob | null;
  audioUrl: string | null;
  
  // Generated Questions
  questions: Question[];
  currentQuestionIndex: number;
  
  // User Answers
  answers: Record<string, string | string[]>;
  
  // Triage Results
  triageData: TriageData | null;
  triageResult: TriageOutcome | null;
  triageId: string | null;
  
  // Flow State
  isCompleted: boolean;
  isLoading: boolean;
  error: string | null;
}

// Actions
type AudioTriageAction =
  | { type: 'SET_SYMPTOMS'; payload: { symptoms: string[]; text: string; blob: Blob | null; audioUrl: string | null } }
  | { type: 'SET_QUESTIONS'; payload: Question[] }
  | { type: 'SET_CURRENT_QUESTION'; payload: number }
  | { type: 'SET_ANSWER'; payload: { questionId: string; answer: string | string[] } }
  | { type: 'SET_TRIAGE_DATA'; payload: TriageData }
  | { type: 'SET_TRIAGE_RESULT'; payload: TriageOutcome }
  | { type: 'SET_TRIAGE_ID'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_COMPLETED'; payload: boolean }
  | { type: 'RESET_STATE' };

// Initial State
const initialState: AudioTriageState = {
  selectedSymptoms: [],
  symptomsText: '',
  recordedBlob: null,
  audioUrl: null,
  questions: [],
  currentQuestionIndex: 0,
  answers: {},
  triageData: null,
  triageResult: null,
  triageId: null,
  isCompleted: false,
  isLoading: false,
  error: null,
};

// Reducer
const audioTriageReducer = (state: AudioTriageState, action: AudioTriageAction): AudioTriageState => {
  switch (action.type) {
    case 'SET_SYMPTOMS':
      return {
        ...state,
        selectedSymptoms: action.payload.symptoms,
        symptomsText: action.payload.text,
        recordedBlob: action.payload.blob,
        audioUrl: action.payload.audioUrl,
      };
    case 'SET_QUESTIONS':
      return {
        ...state,
        questions: action.payload,
        currentQuestionIndex: 0,
      };
    case 'SET_CURRENT_QUESTION':
      return {
        ...state,
        currentQuestionIndex: action.payload,
      };
    case 'SET_ANSWER':
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.payload.questionId]: action.payload.answer,
        },
      };
    case 'SET_TRIAGE_DATA':
      return {
        ...state,
        triageData: action.payload,
      };
    case 'SET_TRIAGE_RESULT':
      return {
        ...state,
        triageResult: action.payload,
      };
    case 'SET_TRIAGE_ID':
      return {
        ...state,
        triageId: action.payload,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
      };
    case 'SET_COMPLETED':
      return {
        ...state,
        isCompleted: action.payload,
      };
    case 'RESET_STATE':
      return initialState;
    default:
      return state;
  }
};

// Context
interface AudioTriageContextType {
  state: AudioTriageState;
  
  // Symptom Actions
  setSymptoms: (symptoms: string[], text: string, blob: Blob | null, audioUrl: string | null) => void;
  
  // Question Actions
  setQuestions: (questions: Question[]) => void;
  setCurrentQuestion: (index: number) => void;
  nextQuestion: () => void;
  previousQuestion: () => void;
  
  // Answer Actions
  setAnswer: (questionId: string, answer: string | string[]) => void;
  getCurrentQuestion: () => Question | null;
  getAnswer: (questionId: string) => string | string[] | undefined;
  
  // Triage Actions
  setTriageData: (data: TriageData) => void;
  setTriageResult: (result: TriageOutcome) => void;
  setTriageId: (id: string) => void;
  
  // State Actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setCompleted: (completed: boolean) => void;
  resetState: () => void;
  
  // Computed Values
  isQuestionAnswered: (questionId: string) => boolean;
  canProceedToNext: () => boolean;
  getCompletedAnswers: () => Record<string, string | string[]>;
}

const AudioTriageContext = createContext<AudioTriageContextType | undefined>(undefined);

// Provider Component
interface AudioTriageProviderProps {
  children: ReactNode;
}

export const AudioTriageProvider: React.FC<AudioTriageProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(audioTriageReducer, initialState);

  // Symptom Actions
  const setSymptoms = (symptoms: string[], text: string, blob: Blob | null, audioUrl: string | null) => {
    dispatch({ type: 'SET_SYMPTOMS', payload: { symptoms, text, blob, audioUrl } });
  };

  // Question Actions
  const setQuestions = (questions: Question[]) => {
    dispatch({ type: 'SET_QUESTIONS', payload: questions });
  };

  const setCurrentQuestion = (index: number) => {
    dispatch({ type: 'SET_CURRENT_QUESTION', payload: index });
  };

  const nextQuestion = () => {
    if (state.currentQuestionIndex < state.questions.length - 1) {
      dispatch({ type: 'SET_CURRENT_QUESTION', payload: state.currentQuestionIndex + 1 });
    }
  };

  const previousQuestion = () => {
    if (state.currentQuestionIndex > 0) {
      dispatch({ type: 'SET_CURRENT_QUESTION', payload: state.currentQuestionIndex - 1 });
    }
  };

  // Answer Actions
  const setAnswer = (questionId: string, answer: string | string[]) => {
    dispatch({ type: 'SET_ANSWER', payload: { questionId, answer } });
  };

  const getCurrentQuestion = (): Question | null => {
    return state.questions[state.currentQuestionIndex] || null;
  };

  const getAnswer = (questionId: string): string | string[] | undefined => {
    return state.answers[questionId];
  };

  // Triage Actions
  const setTriageData = (data: TriageData) => {
    dispatch({ type: 'SET_TRIAGE_DATA', payload: data });
  };

  const setTriageResult = (result: TriageOutcome) => {
    dispatch({ type: 'SET_TRIAGE_RESULT', payload: result });
  };

  const setTriageId = (id: string) => {
    dispatch({ type: 'SET_TRIAGE_ID', payload: id });
  };

  // State Actions
  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  const setError = (error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };

  const setCompleted = (completed: boolean) => {
    dispatch({ type: 'SET_COMPLETED', payload: completed });
  };

  const resetState = () => {
    dispatch({ type: 'RESET_STATE' });
  };

  // Computed Values
  const isQuestionAnswered = (questionId: string): boolean => {
    const answer = state.answers[questionId];
    return answer !== undefined && answer !== '' && (Array.isArray(answer) ? answer.length > 0 : true);
  };

  const canProceedToNext = (): boolean => {
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) return false;
    
    if (currentQuestion.required) {
      return isQuestionAnswered(currentQuestion.id);
    }
    return true;
  };

  const getCompletedAnswers = (): Record<string, string | string[]> => {
    return { ...state.answers };
  };

  const contextValue: AudioTriageContextType = {
    state,
    setSymptoms,
    setQuestions,
    setCurrentQuestion,
    nextQuestion,
    previousQuestion,
    setAnswer,
    getCurrentQuestion,
    getAnswer,
    setTriageData,
    setTriageResult,
    setTriageId,
    setLoading,
    setError,
    setCompleted,
    resetState,
    isQuestionAnswered,
    canProceedToNext,
    getCompletedAnswers,
  };

  return (
    <AudioTriageContext.Provider value={contextValue}>
      {children}
    </AudioTriageContext.Provider>
  );
};

// Hook to use the context
export const useAudioTriage = (): AudioTriageContextType => {
  const context = useContext(AudioTriageContext);
  if (context === undefined) {
    throw new Error('useAudioTriage must be used within an AudioTriageProvider');
  }
  return context;
};
